/* ==========================================================================
   AZURO — progressive enhancements
   --------------------------------------------------------------------------
   Two jobs, both optional by design:
     1. Reveal-on-scroll for `.az-reveal` elements.
     2. Smooth open/close for the `.az-faq` accordions.

   Everything degrades safely. Content is visible without this file, the FAQ
   still opens (native <details>), and nothing here throws if an element is
   missing. Re-runs cleanly when the theme editor swaps a section.
   ========================================================================== */

(function () {
  'use strict';

  var REVEAL_SELECTOR = '.az-reveal';
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------
     The `az-anim` class on <html> is what actually hides elements (see
     azuro.css). We only add it when we are certain we can reveal them again —
     i.e. IntersectionObserver exists and motion is welcome. If anything below
     is unsupported, the class is never added and everything stays visible.
     ---------------------------------------------------------------------- */

  var observer = null;

  function canAnimate() {
    return 'IntersectionObserver' in window && !prefersReducedMotion.matches;
  }

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('az-in');
          releaseAfterReveal(entry.target);
          observer.unobserve(entry.target); // reveal once, never re-hide
        });
      },
      {
        // Fire slightly before the element reaches the viewport so the motion
        // finishes as it settles into view rather than after.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.05
      }
    );

    return observer;
  }

  // `will-change: opacity, transform` in azuro.css buys a compositor layer for
  // the travel. Keeping it afterwards costs one layer per element for the rest
  // of the visit, so hand it back once the element has landed. transitionend
  // can be skipped — background tab, interrupted transition — so a timer
  // guarantees it, the same way the FAQ close does below.
  function releaseAfterReveal(el) {
    var done = function (event) {
      // transitionend bubbles, and these elements have children of their own
      // that transition on hover. Only the reveal's own end counts.
      if (event && event.target !== el) return;
      el.removeEventListener('transitionend', done);
      clearTimeout(fallback);
      el.style.willChange = 'auto';
    };

    // Longest reveal (media, 1050ms) plus the deepest stagger delay (400ms),
    // with room to spare. Overshooting only delays the release.
    var fallback = setTimeout(done, 1600);
    el.addEventListener('transitionend', done);
  }

  function initReveal(root) {
    if (!canAnimate()) return;

    document.documentElement.classList.add('az-anim');

    var targets = (root || document).querySelectorAll(REVEAL_SELECTOR);
    var io = getObserver();

    targets.forEach(function (el) {
      if (el.classList.contains('az-in')) return;

      // Already on screen at load (e.g. above the fold, or a section the
      // merchant just re-rendered in the editor): show it immediately rather
      // than waiting for a scroll that may never come.
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        el.classList.add('az-in');
        releaseAfterReveal(el);
        return;
      }

      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     FAQ accordion
     ------------------------------------------------------------------------
     <details> opens instantly by default. To animate the close as well as the
     open we take over the click: on close we run the transition first, then
     flip `open` once it has finished.
     ---------------------------------------------------------------------- */

  function initFaq(root) {
    var items = (root || document).querySelectorAll('.az-faq__item');

    items.forEach(function (item) {
      if (item.dataset.azFaqReady === 'true') return;
      item.dataset.azFaqReady = 'true';

      var summary = item.querySelector('.az-faq__q');
      var answer = item.querySelector('.az-faq__a');
      if (!summary || !answer) return;

      summary.addEventListener('click', function (event) {
        // Let the browser handle it natively if motion is unwelcome.
        if (prefersReducedMotion.matches) return;

        event.preventDefault();

        if (item.open) {
          // Closing: animate to 0fr, then actually close.
          answer.style.gridTemplateRows = '0fr';

          var onClosed = function () {
            answer.removeEventListener('transitionend', onClosed);
            clearTimeout(fallback);
            item.open = false;
            answer.style.gridTemplateRows = '';
          };

          // transitionend can be skipped (background tab, interrupted
          // transition), so guarantee the close with a timer.
          var fallback = setTimeout(onClosed, 420);
          answer.addEventListener('transitionend', onClosed);
        } else {
          // Opening: reveal first so the row has something to grow into,
          // then animate on the next frame.
          answer.style.gridTemplateRows = '0fr';
          item.open = true;

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              answer.style.gridTemplateRows = '1fr';
            });
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Gallery dots
     ------------------------------------------------------------------------
     The row scrolls natively with snap points; this only keeps the dots in
     sync and lets them scroll the row. Without the script the cards still
     scroll — only the dots go inert, so they are hidden in that case rather
     than left as dead controls.
     ---------------------------------------------------------------------- */

  function initGallery(root) {
    var galleries = (root || document).querySelectorAll('.az-gallery');

    galleries.forEach(function (gallery) {
      if (gallery.dataset.azGalleryReady === 'true') return;
      gallery.dataset.azGalleryReady = 'true';

      var viewport = gallery.querySelector('.az-gallery__viewport');
      var track = gallery.querySelector('[data-az-gallery]');
      var dots = [].slice.call(gallery.querySelectorAll('.az-gallery__dot'));
      if (!viewport || !track || !dots.length) return;

      var cards = [].slice.call(track.children);

      var setActive = function (index) {
        dots.forEach(function (dot, i) {
          var on = i === index;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      };

      // Nearest card to the viewport's left edge wins.
      var sync = function () {
        var left = viewport.scrollLeft;
        var best = 0;
        var bestDistance = Infinity;
        cards.forEach(function (card, i) {
          var d = Math.abs(card.offsetLeft - track.offsetLeft - left);
          if (d < bestDistance) {
            bestDistance = d;
            best = i;
          }
        });
        setActive(best);
      };

      var frame = null;
      viewport.addEventListener(
        'scroll',
        function () {
          if (frame) return;
          frame = requestAnimationFrame(function () {
            frame = null;
            sync();
          });
        },
        { passive: true }
      );

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var card = cards[i];
          if (!card) return;
          viewport.scrollTo({
            left: card.offsetLeft - track.offsetLeft,
            behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
          });
        });
      });

      sync();
    });
  }

  /* ------------------------------------------------------------------------
     Connector switch
     ------------------------------------------------------------------------
     Moves the indicator by setting one custom property, and swaps which panel
     is active. Keyboard arrows work because it is a real tablist.
     ---------------------------------------------------------------------- */

  function initSwitch(root) {
    var roots = (root || document).querySelectorAll('[data-az-switch-root]');

    roots.forEach(function (wrap) {
      if (wrap.dataset.azSwitchReady === 'true') return;
      wrap.dataset.azSwitchReady = 'true';

      var buttons = [].slice.call(wrap.querySelectorAll('[data-az-switch]'));
      var panels = [].slice.call(wrap.querySelectorAll('[data-az-panel]'));
      if (buttons.length < 2 || !panels.length) return;

      var select = function (index) {
        wrap.style.setProperty('--az-switch-index', String(index));

        buttons.forEach(function (b, i) {
          var on = i === index;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });

        panels.forEach(function (p, i) {
          var on = i === index;
          p.classList.toggle('is-active', on);
          if (on) p.removeAttribute('aria-hidden');
          else p.setAttribute('aria-hidden', 'true');
        });
      };

      buttons.forEach(function (b, i) {
        b.addEventListener('click', function () {
          select(i);
        });

        b.addEventListener('keydown', function (event) {
          var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;
          event.preventDefault();
          var next = (i + step + buttons.length) % buttons.length;
          select(next);
          buttons[next].focus();
        });
      });

      select(0);
    });
  }

  /* ------------------------------------------------------------------------
     Spotlight
     ------------------------------------------------------------------------
     The stage is pinned by `position: sticky` in CSS, so scrolling is entirely
     the browser's job. All this does is decide which step is the current one
     and say so with a class — no measuring on scroll, no layout written from
     JavaScript, nothing that can drift out of step with the pinning.

     The current step is whichever one covers the middle of the viewport. That
     is expressed as a rootMargin collapsing the observation area to a thin
     band across the centre: a step is "intersecting" only while it crosses
     that line, which is exactly the definition we want and costs no maths.
     ---------------------------------------------------------------------- */

  function initSpotlight(root) {
    var sections = (root || document).querySelectorAll('[data-az-spotlight]');

    sections.forEach(function (section) {
      if (section.dataset.azSpotlightReady === 'true') return;
      section.dataset.azSpotlightReady = 'true';

      var steps = [].slice.call(section.querySelectorAll('[data-az-spot-step]'));
      var frames = [].slice.call(section.querySelectorAll('[data-az-spot-frame]'));
      var pips = [].slice.call(section.querySelectorAll('[data-az-spot-pip]'));
      if (steps.length < 2 || !frames.length) return;

      var current = -1;

      var setActive = function (index) {
        if (index === current || index < 0) return;
        current = index;

        steps.forEach(function (el, i) {
          el.classList.toggle('is-active', i === index);
        });
        frames.forEach(function (el, i) {
          el.classList.toggle('is-active', i === index);
        });
        pips.forEach(function (el, i) {
          var on = i === index;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-current', on ? 'true' : 'false');
        });
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var index = parseInt(entry.target.getAttribute('data-az-spot-step'), 10);
              if (!isNaN(index)) setActive(index);
            });
          },
          // Top and bottom pulled in to a ~1% band across the middle of the
          // screen. -50%/-49% rather than -50%/-50%, which would be a zero
          // height box that can never intersect anything.
          { rootMargin: '-50% 0px -49% 0px', threshold: 0 }
        );

        steps.forEach(function (step) {
          io.observe(step);
        });
      }

      /* ----------------------------------------------------------------
         Continuous motion
         ----------------------------------------------------------------
         The class toggles above change the picture at four points. On their
         own that reads as four photographs dissolving — which is not what
         happens when you turn an object over. The reference this is modelled
         on moves its subject continuously the whole way down, and swaps what
         you are looking at underneath that movement; the continuity is what
         makes it one object rather than a slideshow.

         So the section also publishes its own scroll progress, 0 to 1, as a
         custom property. CSS uses it to tilt and drift the product the whole
         length of the section, and the cross-fades ride on top.

         Cost control: one rAF-throttled listener, attached only while the
         section is actually near the viewport, doing one measurement and one
         style write per frame. No layout is read back after the write, so it
         cannot thrash.
         ---------------------------------------------------------------- */
      var layout = section.querySelector('.az-spotlight__layout');

      if (layout && !prefersReducedMotion.matches) {
        var ticking = false;
        var listening = false;

        var writeProgress = function () {
          ticking = false;
          var box = layout.getBoundingClientRect();
          // Distance the section travels before its bottom clears the screen.
          var travel = box.height - window.innerHeight;
          if (travel <= 0) return;
          var p = -box.top / travel;
          p = p < 0 ? 0 : p > 1 ? 1 : p;
          section.style.setProperty('--az-spot-p', p.toFixed(4));
        };

        var onScroll = function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(writeProgress);
        };

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            function (entries) {
              var near = entries[0].isIntersecting;
              if (near === listening) return;
              listening = near;
              if (near) {
                window.addEventListener('scroll', onScroll, { passive: true });
                writeProgress();
              } else {
                window.removeEventListener('scroll', onScroll);
              }
            },
            { rootMargin: '100% 0px 100% 0px' }
          ).observe(section);
        } else {
          window.addEventListener('scroll', onScroll, { passive: true });
        }

        writeProgress();
      }

      // Jumping from the rail. Scrolls the step to the middle of the screen,
      // which is where the observer considers a step current — so the class
      // and the position agree instead of fighting.
      pips.forEach(function (pip, i) {
        pip.addEventListener('click', function () {
          var step = steps[i];
          if (!step) return;
          var box = step.getBoundingClientRect();
          var target = window.scrollY + box.top + box.height / 2 - window.innerHeight / 2;
          window.scrollTo({
            top: target,
            behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
          });
        });
      });

      setActive(0);
    });
  }

  /* ---------------------------------------------------------------------- */

  /* ------------------------------------------------------------------------
     Sticky buy bar
     ------------------------------------------------------------------------
     Three jobs: reveal the bar once the page's own buy button is out of view,
     point its submit button at the real product form, and keep its price in
     step with the variant the visitor picked.

     The bar ships with the `hidden` attribute. Everything below either removes
     it deliberately or leaves it alone — so a browser that throws anywhere in
     here shows no bar at all rather than a bar whose button does nothing.
     ---------------------------------------------------------------------- */

  function initBuyBar(root) {
    var bar = (root || document).querySelector('[data-az-buybar]');
    if (!bar) return;

    var submit = bar.querySelector('[data-az-buybar-submit]');

    // On a product page the button must submit the page's real form, so that
    // the selected variant, the quantity and every app hooked into that form
    // are all respected. The form lives in a different section and its id is
    // built from that section's id, so it cannot be known from the bar's own
    // Liquid — it is resolved here instead.
    if (submit) {
      // Find the form by way of the page's OWN add button rather than by
      // querying for a cart form directly. Dawn renders a second, hidden form
      // that also posts to /cart/add — the Shop Pay instalments one — and it
      // comes first in the document. Submitting that one would add to the cart
      // while bypassing the real form's quantity, line-item properties and
      // every app that hooks into it.
      var pageAddButton = document.querySelector('button[name="add"]:not([data-az-buybar-submit])');
      var form = pageAddButton ? pageAddButton.form : null;
      // `getAttribute('id')`, never `form.id`. A form exposes its named
      // controls as properties, and the product form contains
      // `<input name="id">` for the variant — so `form.id` returns that input
      // element rather than the form's id string, and the truthiness check
      // below would pass while the attribute we set was garbage.
      var formId = form ? form.getAttribute('id') : null;
      if (!formId) {
        // No form to submit means the button would be a dead control. Leaving
        // the bar hidden is the correct outcome.
        return;
      }
      submit.setAttribute('form', formId);
    }

    // Mirror the real price element, whatever updates it. Dawn re-renders the
    // price node when the variant changes, and reading it back is robust to
    // how that happens — no dependency on a particular custom event name.
    var priceOut = bar.querySelector('[data-az-buybar-price]');
    var priceSrc = document.querySelector('.price__container');
    if (priceOut && priceSrc && 'MutationObserver' in window) {
      var syncPrice = function () {
        var text = priceSrc.textContent.replace(/\s+/g, ' ').trim();
        if (text) priceOut.textContent = text;
      };
      new MutationObserver(syncPrice).observe(priceSrc, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Reveal once the page's own call to action has left the viewport. Without
    // IntersectionObserver the bar stays hidden: the page then behaves exactly
    // as it did before this section existed.
    if (!('IntersectionObserver' in window)) return;

    var selector = bar.getAttribute('data-az-buybar-watch');
    var watched = selector ? document.querySelectorAll(selector) : [];
    if (!watched.length) return;

    var onScreen = 0;

    var show = function (visible) {
      bar.hidden = false;
      bar.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('az-buybar-visible', visible);
    };

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          onScreen += entry.isIntersecting ? 1 : -1;
        });
        if (onScreen < 0) onScreen = 0;
        show(onScreen === 0);
      },
      // A little slack at the bottom, so the bar does not flick in while the
      // real button is still half on screen and obviously usable.
      { rootMargin: '0px 0px -80px 0px' }
    );

    Array.prototype.forEach.call(watched, function (el) {
      io.observe(el);
    });
  }

  function init(root) {
    try {
      // Marks that the enhancements are running. Controls that would be inert
      // without JavaScript (the gallery dots) stay hidden until this lands.
      document.documentElement.classList.add('az-js');
      initReveal(root);
      initFaq(root);
      initGallery(root);
      initSwitch(root);
      initSpotlight(root);
      initBuyBar(root);
    } catch (error) {
      // Never let an enhancement break the page. Make sure nothing is left
      // hidden if we failed partway through.
      document.documentElement.classList.remove('az-anim');
      if (window.console && console.warn) console.warn('[azuro]', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init whatever section the merchant just changed.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
